import React, { memo, useMemo } from 'react';
import clsx from 'clsx';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Button from '@/elements/Button/Button.component';
import Icon from '@/elements/Icon/Icon.component';

import { IProps } from '@/blocks/MetaDataBlock/MetaDataBlock.interface';
import type { ILink } from '@/types/base';
import { IconNames } from '@/elements/Icon/Icon.interface';
import '@/blocks/MetaDataBlock/MetaDataBlock.styles.css';

interface MetaDataBlockItemProps {
  title?: string;
  children: React.ReactNode;
  type?: 'text' | 'pill' | 'link';
  className?: string;
}

const MetaDataBlockItem = memo<MetaDataBlockItemProps>(
  ({ title, children, type, className }) => (
    <div
      className={clsx(
        'metaDataBlock__item metaDataBlock__description',
        className,
        {
          [`metaDataBlock__description--${type}`]: type,
        },
      )}
    >
      {title && <p className="metaDataBlock__title">{title}</p>}
      <div>
        <div className="metaDataBlock__text">{children}</div>
      </div>
    </div>
  ),
);

MetaDataBlockItem.displayName = 'MetaDataBlockItem';

interface SocialInfoProps {
  icon: IconNames;
  url: string;
  label: string;
  target?: HTMLAnchorElement['target'];
  className?: string;
}

const SocialInfo = memo<SocialInfoProps>(
  ({ icon, url, label, target = '_blank' }) => (
    <a
      href={url}
      target={target}
      rel="noopener noreferrer"
      aria-label={label}
      className="metaDataBlock__socialLinks--item"
    >
      <Icon icon={icon} />
    </a>
  ),
);

SocialInfo.displayName = 'SocialInfo';

const MetaDataBlock: React.FC<IProps> = ({
  items,
  className,
  socialLinks,
  shareInfo,
  buttons,
  backgroundColor = 'grey-25',
  alignment = 'horizontal',
  ...blockWrapperProps
}) => {
  const hasSharing = useMemo(() => {
    return (socialLinks && socialLinks.length > 0) || shareInfo;
  }, [socialLinks, shareInfo]);

  const shareEmailUrl = useMemo(() => {
    if (!shareInfo) return '';

    const { shareUrl, mailSubject, mailBody, mailTo = '' } = shareInfo;
    return `mailto:${mailTo}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}: ${shareUrl}`;
  }, [shareInfo]);
  const hasButtons = useMemo(() => {
    return buttons && buttons.length > 0;
  }, [buttons]);

  const renderLinkItems = (links: ILink[]) => {
    return links.map((link, idx) => {
      if (typeof link !== 'object' || link === null) {
        return null;
      }

      return (
        <a
          key={`link-${idx}`}
          href={link.href}
          target={link.target || undefined}
          title={link.title || undefined}
          aria-label={link.ariaLabel || undefined}
          rel="noopener noreferrer"
          className="metaDataBlock__text--link"
        >
          {link?.text}
        </a>
      );
    });
  };

  const renderPillItems = (items: string[]) => {
    return items.map((item, idx) => (
      <span key={`pill-${idx}`} className="metaDataBlock__text--pill">
        {item}
      </span>
    ));
  };

  const renderItemContent = (
    text: string | string[] | ILink[],
    type?: 'text' | 'pill' | 'link',
  ) => {
    // Handle string content
    if (typeof text === 'string') {
      return <span className={`metaDataBlock__text--${type}`}>{text}</span>;
    }

    // Handle array content
    if (Array.isArray(text)) {
      if (type === 'link') {
        return renderLinkItems(text as ILink[]);
      }
      return renderPillItems(text as string[]);
    }

    return null;
  };

  return (
    <BlockWrapper
      className={clsx('metaDataBlock', className)}
      {...blockWrapperProps}
    >
      <ContentWrapper>
        <ContentColumn
          className={clsx('metaDataBlock__content', {
            [`bg-${backgroundColor}`]: backgroundColor,
            [`metaDataBlock__content--${alignment}`]: alignment,
          })}
        >
          {' '}
          {items?.map(
            ({ title, text, type }, index) =>
              text && (
                <MetaDataBlockItem
                  key={`item-${index}`}
                  title={title}
                  type={type}
                >
                  {renderItemContent(text, type)}
                </MetaDataBlockItem>
              ),
          )}
          {hasSharing && (
            <MetaDataBlockItem title="Share">
              <div className="metaDataBlock__socialLinks">
                {socialLinks?.map((link, index) => (
                  <SocialInfo
                    key={index}
                    icon={link.icon}
                    url={link.url}
                    label={link.label}
                  />
                ))}

                {shareInfo && (
                  <SocialInfo
                    icon="envelope"
                    url={shareEmailUrl}
                    label="Share via email"
                  />
                )}
              </div>
            </MetaDataBlockItem>
          )}
          {hasButtons && (
            <MetaDataBlockItem>
              <div className="metaDataBlock__buttons">
                {buttons!.map((buttonProps, index) => (
                  <Button key={index} {...buttonProps} />
                ))}
              </div>
            </MetaDataBlockItem>
          )}
        </ContentColumn>
      </ContentWrapper>
    </BlockWrapper>
  );
};

MetaDataBlock.displayName = 'MetaDataBlock';

export default memo(MetaDataBlock);
